import { Box, Divider, Grid, Typography } from '@mui/material';

import type { ServiceSchedulePricing, VehicleSchedulePricing, ZoneFormState } from '../zoneTypes';

import type { ZonePricingInput } from '../../../utils/zoneFareCalculator';

import { isRideHailingService, isFoodOrGroceryDelivery, usesZonePartnerPricing, activeServicePricing, activeVehiclePricing, isOnDemandPricingRow } from '../zoneTypes';
import { chargeFieldLabel } from '../../../utils/currency';
import {
  ZoneAmountNumberField,
  ZoneDistanceNumberField,
  ZoneWholeNumberField,
} from './ZonePricingNumberField';

interface Props {
  form: ZoneFormState;
  setForm: (f: ZoneFormState) => void;
  scheduleType: 'ride_now' | 'ride_later';
  unit: string;
  currency: string;
}

function PricingFields({
  pricing, onChange, unit, currency, hubType, slug,
}: {
  pricing: ZonePricingInput;
  onChange: (p: ZonePricingInput) => void;
  unit: string;
  currency: string;
  hubType: string;
  slug: string;
}) {
  const isDelivery = hubType === 'delivery';
  const isFoodGrocery = isFoodOrGroceryDelivery(slug);
  const isCourier = slug === 'courier-service';
  const isRental = slug.includes('rental');

  return (
    <Grid container spacing={1.5}>
      {!isDelivery && (
        <>
          <Grid item xs={6} md={4}>
            <ZoneDistanceNumberField
              label={`Base distance (${unit})`}
              value={pricing.baseDistanceKm ?? 0}
              onValueChange={(n) => onChange({ ...pricing, baseDistanceKm: n })}
            />
          </Grid>
          <Grid item xs={6} md={4}>
            <ZoneAmountNumberField
              label={chargeFieldLabel('Base price', currency)}
              currency={currency}
              value={pricing.baseFare ?? 0}
              onValueChange={(n) => onChange({ ...pricing, baseFare: n })}
            />
          </Grid>
          <Grid item xs={6} md={4}>
            <ZoneAmountNumberField
              label={chargeFieldLabel(`Per-${unit} charge`, currency)}
              currency={currency}
              value={pricing.perKmRate ?? 0}
              onValueChange={(n) => onChange({ ...pricing, perKmRate: n })}
            />
          </Grid>
        </>
      )}
      {isDelivery && isFoodGrocery && (
        <>
          <Grid item xs={6} md={4}>
            <ZoneDistanceNumberField
              label={`Base distance (${unit})`}
              value={pricing.baseDistanceKm ?? 0}
              onValueChange={(n) => onChange({ ...pricing, baseDistanceKm: n })}
            />
          </Grid>
          <Grid item xs={6} md={4}>
            <ZoneAmountNumberField
              label={chargeFieldLabel('Delivery charge', currency)}
              currency={currency}
              value={pricing.deliveryCharge ?? 0}
              onValueChange={(n) => onChange({ ...pricing, deliveryCharge: n })}
            />
          </Grid>
          <Grid item xs={6} md={4}>
            <ZoneAmountNumberField
              label={chargeFieldLabel(`Per-${unit} charge after base`, currency)}
              currency={currency}
              value={pricing.perKmRate ?? 0}
              onValueChange={(n) => onChange({ ...pricing, perKmRate: n })}
            />
          </Grid>
        </>
      )}
      {isDelivery && !isFoodGrocery && (
        <Grid item xs={6} md={4}>
          <ZoneAmountNumberField
            label={chargeFieldLabel('Delivery charge', currency)}
            currency={currency}
            value={pricing.deliveryCharge ?? 0}
            onValueChange={(n) => onChange({ ...pricing, deliveryCharge: n })}
          />
        </Grid>
      )}
      {!isRental && !isDelivery && (
        <Grid item xs={6} md={4}>
          <ZoneAmountNumberField
            label={chargeFieldLabel('Price per min', currency)}
            currency={currency}
            value={pricing.perMinRate ?? 0}
            onValueChange={(n) => onChange({ ...pricing, perMinRate: n })}
          />
        </Grid>
      )}
      {isCourier && (
        <Grid item xs={6} md={4}>
          <ZoneAmountNumberField
            label={chargeFieldLabel('Per kg rate', currency)}
            currency={currency}
            value={pricing.perKgRate ?? 0}
            onValueChange={(n) => onChange({ ...pricing, perKgRate: n })}
          />
        </Grid>
      )}
      {isRental && slug.includes('bike') && (
        <Grid item xs={6} md={4}>
          <ZoneAmountNumberField
            label={chargeFieldLabel('Per hour', currency)}
            currency={currency}
            value={pricing.perHour ?? 0}
            onValueChange={(n) => onChange({ ...pricing, perHour: n })}
          />
        </Grid>
      )}
      {isRental && slug.includes('car') && (
        <Grid item xs={6} md={4}>
          <ZoneAmountNumberField
            label={chargeFieldLabel('Per day', currency)}
            currency={currency}
            value={pricing.perDay ?? 0}
            onValueChange={(n) => onChange({ ...pricing, perDay: n })}
          />
        </Grid>
      )}
      <Grid item xs={6} md={4}>
        <ZoneWholeNumberField
          label="Free waiting (min)"
          value={pricing.freeWaitingMin ?? 0}
          onValueChange={(n) => onChange({ ...pricing, freeWaitingMin: n })}
        />
      </Grid>
      <Grid item xs={6} md={4}>
        <ZoneAmountNumberField
          label={chargeFieldLabel('Waiting charge/min', currency)}
          currency={currency}
          value={pricing.waitingChargePerMin ?? 0}
          onValueChange={(n) => onChange({ ...pricing, waitingChargePerMin: n })}
        />
      </Grid>
      <Grid item xs={6} md={4}>
        <ZoneAmountNumberField
          label={chargeFieldLabel('Minimum fare', currency)}
          currency={currency}
          value={pricing.minimumFare ?? 0}
          onValueChange={(n) => onChange({ ...pricing, minimumFare: n })}
        />
      </Grid>
    </Grid>
  );
}

function updateServicePricing(
  list: ServiceSchedulePricing[],
  serviceId: string,
  scheduleType: 'ride_now' | 'ride_later',
  pricing: ZonePricingInput,
): ServiceSchedulePricing[] {
  return list.map((sp) => {
    if (sp.serviceId !== serviceId) return sp;
    return scheduleType === 'ride_now' ? { ...sp, rideNow: pricing } : { ...sp, rideLater: pricing };
  });
}

function updateVehiclePricing(
  list: VehicleSchedulePricing[],
  vehicleTypeCode: string,
  scheduleType: 'ride_now' | 'ride_later',
  pricing: ZonePricingInput,
): VehicleSchedulePricing[] {
  return list.map((vp) => {
    if (vp.vehicleTypeCode !== vehicleTypeCode) return vp;
    return scheduleType === 'ride_now' ? { ...vp, rideNow: pricing } : { ...vp, rideLater: pricing };
  });
}

export default function ZoneSchedulePricingStep({ form, setForm, scheduleType, unit, currency }: Props) {
  const title = scheduleType === 'ride_now' ? 'Ride Now pricing' : 'Ride Later pricing';
  const otherServices = activeServicePricing(
    form,
    (slug) => !isRideHailingService(slug) && !usesZonePartnerPricing(slug),
  ).filter((sp) => !isOnDemandPricingRow(sp));
  const transportVehicles = activeVehiclePricing(form);
  const hasRows = transportVehicles.length > 0 || otherServices.length > 0;

  if (!hasRows) {
    return <Typography color="text.secondary">Select services and transport vehicles in Step 1.</Typography>;
  }

  return (
    <Box>
      <Typography variant="subtitle1" fontWeight={700} gutterBottom>{title}</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Set base distance, base price, per km, and waiting charges for each vehicle or service.
      </Typography>

      {transportVehicles.map((vp, idx) => (
        <Box key={vp.vehicleTypeCode} sx={{ mb: 2 }}>
          <Typography variant="subtitle2" fontWeight={700} sx={{ textTransform: 'uppercase' }}>{vp.vehicleTypeName}</Typography>
          <Typography variant="caption" color="text.secondary">Transport · vehicle type</Typography>
          <Box sx={{ mt: 1 }}>
            <PricingFields
              pricing={scheduleType === 'ride_now' ? vp.rideNow : vp.rideLater}
              onChange={(p) => setForm({
                ...form,
                vehiclePricing: updateVehiclePricing(form.vehiclePricing, vp.vehicleTypeCode, scheduleType, p),
              })}
              unit={unit}
              currency={currency}
              hubType="transport"
              slug="transport"
            />
          </Box>
          {idx < transportVehicles.length - 1 && <Divider sx={{ mt: 2 }} />}
        </Box>
      ))}

      {transportVehicles.length > 0 && otherServices.length > 0 && <Divider sx={{ my: 2 }} />}

      {otherServices.map((sp, idx) => (
        <Box key={sp.serviceId} sx={{ mb: 2 }}>
          <Typography variant="subtitle2" fontWeight={700} sx={{ textTransform: 'uppercase' }}>{sp.serviceName}</Typography>
          <Typography variant="caption" color="text.secondary">{sp.hubType} · {sp.slug}</Typography>
          <Box sx={{ mt: 1 }}>
            <PricingFields
              pricing={scheduleType === 'ride_now' ? sp.rideNow : sp.rideLater}
              onChange={(p) => setForm({ ...form, servicePricing: updateServicePricing(form.servicePricing, sp.serviceId, scheduleType, p) })}
              unit={unit}
              currency={currency}
              hubType={sp.hubType}
              slug={sp.slug}
            />
          </Box>
          {idx < otherServices.length - 1 && <Divider sx={{ mt: 2 }} />}
        </Box>
      ))}
    </Box>
  );
}
